import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  FileCode2,
  Route as RouteIcon,
  Boxes,
  FileText,
  Files,
  AlertTriangle,
  Activity,
  Pencil,
  PackagePlus,
  Package,
  Upload,
  PenTool,
  Users,
  Settings,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Skeleton from '../components/ui/Skeleton';
import './Dashboard.css';

const REFRESH_MS = 60_000;
const ATIVIDADE_LIMIT_INICIAL = 15;
const ATIVIDADE_LIMIT_PASSO = 15;

function saudacao() {
  const hora = new Date().getHours();
  if (hora < 12) return 'Bom dia';
  if (hora < 18) return 'Boa tarde';
  return 'Boa noite';
}

function primeiroNome(user) {
  if (user?.full_name) return user.full_name.trim().split(/\s+/)[0];
  return user?.username || '';
}

function dataPorExtenso() {
  const texto = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function formatarTempo(min) {
  if (min == null) return '—';
  if (min >= 60) {
    return `${(min / 60).toFixed(1).replace('.', ',')} h`;
  }
  return `${min} min`;
}

function formatarHora(isoString) {
  const data = new Date(isoString);
  const agora = new Date();
  const diffMs = agora - data;
  if (diffMs < 24 * 60 * 60 * 1000 && data.getDate() === agora.getDate()) {
    return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

const ICONE_POR_TIPO = {
  ROTEIRO_GERADO: RouteIcon,
  PDF_GERADO: FileText,
  PDF_LOTE_GERADO: Files,
  PALETIZACAO_GERADA: Boxes,
  CONVERSAO_STEP: FileCode2,
  CONVERSAO_ERRO: AlertTriangle,
  batch: PackagePlus,
  create: PackagePlus,
  update: Pencil,
  delete: Pencil,
};

function iconeDoTipo(tipo) {
  return ICONE_POR_TIPO[tipo] || Activity;
}

// Mesmo mapeamento de ícones da sidebar (layouts/Sidebar.jsx) — rota → ícone.
const ICONE_POR_ROTA = {
  '/pecas': Package,
  '/pecas/importar': Upload,
  '/roteiro': RouteIcon,
  '/paletizacao': Boxes,
  '/editor': PenTool,
  '/conversor-step': FileCode2,
  '/admin/usuarios': Users,
  '/admin/operacoes': Settings,
};

function iconeDoModulo(rota) {
  return ICONE_POR_ROTA[rota] || Activity;
}

function MetricCard({ titulo, valor, badge, erro, carregando }) {
  return (
    <Card className="cw-metric-card">
      <div className="cw-metric-label">{titulo}</div>
      {carregando ? (
        <Skeleton width="70%" height="34px" />
      ) : erro ? (
        <div className="cw-metric-valor cw-metric-valor--erro" title={erro}>—</div>
      ) : (
        <div className="cw-metric-valor">
          {valor}
          {badge}
        </div>
      )}
    </Card>
  );
}

function AtividadeSkeleton() {
  return (
    <div className="cw-atividade-lista">
      {[0, 1, 2, 3].map((i) => (
        <div className="cw-atividade-item" key={i}>
          <Skeleton width="32px" height="32px" style={{ borderRadius: '50%' }} />
          <div className="cw-atividade-textos">
            <Skeleton width="60%" height="14px" />
            <Skeleton width="35%" height="12px" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ModulosSkeleton() {
  return (
    <div className="cw-modulos-lista">
      {[0, 1, 2, 3].map((i) => (
        <div className="cw-modulos-item" key={i}>
          <Skeleton width="16px" height="16px" />
          <Skeleton width="80%" height="12px" />
          <Skeleton width="100%" height="6px" />
          <Skeleton width="18px" height="12px" />
        </div>
      ))}
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [periodo, setPeriodo] = useState('hoje');
  const [metricas, setMetricas] = useState(null);
  const [metricasCarregando, setMetricasCarregando] = useState(true);
  const [metricasErro, setMetricasErro] = useState(null);

  const [atividade, setAtividade] = useState(null);
  const [atividadeLimit, setAtividadeLimit] = useState(ATIVIDADE_LIMIT_INICIAL);
  const [atividadeCarregando, setAtividadeCarregando] = useState(true);
  const [atividadeErro, setAtividadeErro] = useState(null);

  const [modulosUsados, setModulosUsados] = useState(null);
  const [modulosCarregando, setModulosCarregando] = useState(true);
  const [modulosErro, setModulosErro] = useState(null);

  const carregarMetricas = useCallback(async (periodoAtual, { silencioso = false } = {}) => {
    if (!silencioso) setMetricasCarregando(true);
    setMetricasErro(null);
    try {
      const { data } = await api.get('/dashboard/metricas', { params: { periodo: periodoAtual } });
      setMetricas(data);
    } catch (err) {
      console.error('dashboard metricas:', err);
      setMetricasErro('Falha ao carregar métricas — tente atualizar a página');
    } finally {
      setMetricasCarregando(false);
    }
  }, []);

  const carregarAtividade = useCallback(async (limit, { silencioso = false } = {}) => {
    if (!silencioso) setAtividadeCarregando(true);
    setAtividadeErro(null);
    try {
      const { data } = await api.get('/dashboard/atividade', { params: { limit } });
      setAtividade(data);
    } catch (err) {
      console.error('dashboard atividade:', err);
      setAtividadeErro('Falha ao carregar atividade recente');
    } finally {
      setAtividadeCarregando(false);
    }
  }, []);

  const carregarModulosUsados = useCallback(async (periodoAtual, { silencioso = false } = {}) => {
    if (!silencioso) setModulosCarregando(true);
    setModulosErro(null);
    try {
      const { data } = await api.get('/dashboard/modulos-mais-usados', { params: { periodo: periodoAtual } });
      setModulosUsados(data.modulos);
    } catch (err) {
      console.error('dashboard modulos-mais-usados:', err);
      setModulosErro('Falha ao carregar módulos mais usados');
    } finally {
      setModulosCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregarMetricas(periodo);
  }, [periodo, carregarMetricas]);

  useEffect(() => {
    carregarAtividade(atividadeLimit);
  }, [atividadeLimit, carregarAtividade]);

  useEffect(() => {
    carregarModulosUsados(periodo);
  }, [periodo, carregarModulosUsados]);

  // Polling leve — atualiza em segundo plano sem piscar skeleton.
  const periodoRef = useRef(periodo);
  periodoRef.current = periodo;
  const atividadeLimitRef = useRef(atividadeLimit);
  atividadeLimitRef.current = atividadeLimit;
  useEffect(() => {
    const id = setInterval(() => {
      carregarMetricas(periodoRef.current, { silencioso: true });
      carregarAtividade(atividadeLimitRef.current, { silencioso: true });
      carregarModulosUsados(periodoRef.current, { silencioso: true });
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [carregarMetricas, carregarAtividade, carregarModulosUsados]);

  const handleAtualizarFeed = () => {
    carregarMetricas(periodo, { silencioso: true });
    carregarAtividade(atividadeLimit, { silencioso: true });
    carregarModulosUsados(periodo, { silencioso: true });
  };

  const erroConversao = metricas?.erros_conversao ?? 0;

  return (
    <div className="cw-dashboard">
      <div className="cw-dashboard-header">
        <div>
          <h1 className="cw-page-title">{saudacao()}, {primeiroNome(user)}</h1>
          <p className="cw-page-subtitle">{dataPorExtenso()}</p>
        </div>
        <div className="cw-periodo-toggle" role="group" aria-label="Período">
          <button
            type="button"
            className={`cw-periodo-btn${periodo === 'hoje' ? ' cw-periodo-btn--ativo' : ''}`}
            onClick={() => setPeriodo('hoje')}
          >
            Hoje
          </button>
          <button
            type="button"
            className={`cw-periodo-btn${periodo === '7dias' ? ' cw-periodo-btn--ativo' : ''}`}
            onClick={() => setPeriodo('7dias')}
          >
            7 dias
          </button>
        </div>
      </div>

      <div className="cw-metric-grid">
        <MetricCard
          titulo="Peças processadas"
          valor={metricas?.pecas_processadas ?? 0}
          carregando={metricasCarregando}
          erro={metricasErro}
        />
        <MetricCard
          titulo="Tempo economizado"
          valor={formatarTempo(metricas?.tempo_economizado_min)}
          carregando={metricasCarregando}
          erro={metricasErro}
        />
        <MetricCard
          titulo="Erros de conversão"
          valor={erroConversao}
          badge={
            !metricasCarregando && !metricasErro && (
              <Badge
                variant={erroConversao > 0 ? 'danger' : 'success'}
                className="cw-metric-badge"
              >
                {erroConversao > 0 ? 'atenção' : 'ok'}
              </Badge>
            )
          }
          carregando={metricasCarregando}
          erro={metricasErro}
        />
      </div>

      <div className="cw-acoes-rapidas">
        <Button variant="secondary" onClick={() => navigate('/conversor-step')}>
          <FileCode2 size={16} /> Nova conversão
        </Button>
        <Button variant="secondary" onClick={() => navigate('/roteiro')}>
          <RouteIcon size={16} /> Gerar roteiro
        </Button>
        <Button variant="secondary" onClick={() => navigate('/paletizacao')}>
          <Boxes size={16} /> Paletização
        </Button>
      </div>

      <div className="cw-dashboard-cols">
        <Card className="cw-atividade-card">
          <div className="cw-atividade-header">
            <h3 className="cw-section-title">Atividade recente</h3>
            <button
              type="button"
              className="cw-atividade-refresh"
              onClick={handleAtualizarFeed}
              aria-label="Atualizar atividade"
              title="Atualizar"
            >
              <RefreshCw size={15} />
            </button>
          </div>

          {atividadeCarregando ? (
            <AtividadeSkeleton />
          ) : atividadeErro ? (
            <p className="cw-atividade-vazio" title={atividadeErro}>
              Não foi possível carregar a atividade recente — tente atualizar.
            </p>
          ) : !atividade || atividade.length === 0 ? (
            <p className="cw-atividade-vazio">
              Nenhuma atividade ainda — as operações aparecem aqui conforme a equipe usa a plataforma.
            </p>
          ) : (
            <>
              <div className="cw-atividade-lista">
                {atividade.map((item, idx) => {
                  const Icone = iconeDoTipo(item.tipo);
                  return (
                    <div className="cw-atividade-item" key={`${item.criado_em}-${idx}`}>
                      <span className="cw-atividade-icone">
                        <Icone size={16} />
                      </span>
                      <div className="cw-atividade-textos">
                        <div className="cw-atividade-titulo">{item.titulo}</div>
                        {item.meta && <div className="cw-atividade-meta">{item.meta}</div>}
                      </div>
                      <div className="cw-atividade-hora">{formatarHora(item.criado_em)}</div>
                    </div>
                  );
                })}
              </div>
              {atividade.length === atividadeLimit && (
                <div className="cw-atividade-vermais">
                  <Button
                    variant="secondary"
                    onClick={() => setAtividadeLimit((v) => v + ATIVIDADE_LIMIT_PASSO)}
                  >
                    Ver mais
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>

        <Card className="cw-modulos-card">
          <h3 className="cw-section-title">Módulos mais usados</h3>

          {modulosCarregando ? (
            <ModulosSkeleton />
          ) : modulosErro ? (
            <p className="cw-modulos-erro" title={modulosErro}>—</p>
          ) : !modulosUsados || modulosUsados.length === 0 ? (
            <p className="cw-modulos-vazio">Sem dados de uso no período</p>
          ) : (
            <div className="cw-modulos-lista">
              {(() => {
                const max = Math.max(...modulosUsados.map((m) => m.acessos), 1);
                return modulosUsados.map((m) => {
                  const Icone = iconeDoModulo(m.rota);
                  return (
                    <div className="cw-modulos-item" key={m.rota}>
                      <span className="cw-modulos-icone">
                        <Icone size={16} />
                      </span>
                      <span className="cw-modulos-nome" title={m.nome_exibicao}>{m.nome_exibicao}</span>
                      <span className="cw-modulos-barra-track">
                        <span
                          className="cw-modulos-barra-fill"
                          style={{ width: `${(m.acessos / max) * 100}%` }}
                        />
                      </span>
                      <span className="cw-modulos-contagem">{m.acessos}</span>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default Dashboard;
